import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ThemeToggle } from '../lib/ThemeToggle.js';
import { type ActivateResponse, type AdminMe, type CustomerMe, api } from '../lib/api.js';

/**
 * 라이선스 활성화 페이지. 본사 admin 또는 고객사 사용자 로그인 필수.
 * - admin: 모든 라이선스 활성화 가능 (대리 활성화).
 * - customer: 본인 회사의 라이선스만 가능.
 */
interface ActivateError {
  message: string;
  code?: string;
}

interface Viewer {
  kind: 'admin' | 'customer';
  email: string;
  displayName: string;
}

function extractChallenge(input: string): { value: string; unwrapped: boolean } {
  const trimmed = input.trim();
  if (trimmed.startsWith('{')) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (
        parsed &&
        typeof parsed === 'object' &&
        'challenge' in parsed &&
        typeof (parsed as { challenge: unknown }).challenge === 'string'
      ) {
        return { value: (parsed as { challenge: string }).challenge.trim(), unwrapped: true };
      }
    } catch {
      // ignore parse error
    }
  }
  return { value: trimmed, unwrapped: false };
}

export function Activate() {
  const navigate = useNavigate();
  const [viewer, setViewer] = useState<Viewer | null>(null);
  const [checking, setChecking] = useState(true);
  const [challenge, setChallenge] = useState('');
  const [result, setResult] = useState<ActivateResponse | null>(null);
  const [error, setError] = useState<ActivateError | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [unwrappedNotice, setUnwrappedNotice] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await api.get<AdminMe>('/api/admin/me');
        if (!cancelled) {
          setViewer({ kind: 'admin', email: me.email, displayName: me.displayName });
          setChecking(false);
        }
        return;
      } catch {
        // not admin
      }
      try {
        const me = await api.get<CustomerMe>('/api/customer/me');
        if (!cancelled) {
          setViewer({ kind: 'customer', email: me.email, displayName: me.displayName });
          setChecking(false);
        }
        return;
      } catch {
        // not customer
      }
      if (!cancelled) {
        navigate('/customer/login?next=/activate', { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  async function logout() {
    if (!viewer) return;
    const path = viewer.kind === 'admin' ? '/api/admin/logout' : '/api/customer/logout';
    await api.post(path, {});
    navigate(viewer.kind === 'admin' ? '/login' : '/customer/login', { replace: true });
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);
    setCopied(false);
    const { value: challengeStr, unwrapped } = extractChallenge(challenge);
    setUnwrappedNotice(unwrapped);
    try {
      const res = await api.post<ActivateResponse>('/api/activate', {
        challenge: challengeStr,
      });
      setResult(res);
    } catch (err) {
      const detail = (err as Error & { detail?: Record<string, unknown> }).detail;
      const friendly = typeof detail?.message === 'string' ? detail.message : null;
      const code = typeof detail?.code === 'string' ? detail.code : undefined;
      setError({
        message: friendly ?? (err as Error).message,
        code,
      });
    } finally {
      setLoading(false);
    }
  }

  async function copyToken() {
    if (!result) return;
    await navigator.clipboard.writeText(result.activationToken);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center text-neutral-500">로딩…</div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="fixed top-4 right-4 z-10">
        <ThemeToggle />
      </div>
      <div className="w-full max-w-2xl bg-surface rounded-xl shadow-sm border border-neutral-200 p-8">
        <header className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">aisight 라이선스 활성화</h1>
            <p className="mt-2 text-neutral-600 text-sm">
              고객사 aisight 서버에서{' '}
              <code className="px-1.5 py-0.5 bg-neutral-100 rounded">imctl license challenge</code>{' '}
              로 생성한 challenge 코드를 아래에 붙여넣으세요. base64 / JSON 봉투 둘 다 자동 인식.
            </p>
          </div>
          {viewer && (
            <div className="text-xs text-neutral-500 text-right shrink-0">
              <div>{viewer.displayName}</div>
              <div className="font-mono">{viewer.email}</div>
              <div className="mt-0.5 inline-block px-1.5 py-0.5 rounded bg-neutral-100">
                {viewer.kind === 'admin' ? '관리자' : '고객사'}
              </div>
              <button
                type="button"
                onClick={logout}
                className="block mt-1 text-neutral-500 hover:text-neutral-800"
              >
                로그아웃
              </button>
            </div>
          )}
        </header>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label htmlFor="challenge" className="block text-sm font-medium text-neutral-800 mb-2">
              Challenge 코드
            </label>
            <textarea
              id="challenge"
              value={challenge}
              onChange={(e) => setChallenge(e.target.value)}
              required
              rows={6}
              spellCheck={false}
              className="w-full px-3 py-2 font-mono text-xs bg-neutral-50 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400"
              placeholder="eyJod2lkIjoiYWJjZGVmIiwiYXBwVmVyc2lvbiI6IjIwMjYuNS4xIiwidGltZXN0YW1wIjoi..."
            />
          </div>
          <button
            type="submit"
            disabled={loading || !challenge.trim()}
            className="w-full px-4 py-2 bg-brand-400 hover:bg-brand-600 disabled:bg-neutral-300 text-white font-medium rounded-md transition-colors"
          >
            {loading ? '활성화 중…' : '활성화'}
          </button>
        </form>

        {unwrappedNotice && !error && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
            <p className="text-xs text-amber-800">
              JSON 봉투에서 challenge 값을 자동으로 추출해 전송했습니다.
            </p>
          </div>
        )}

        {error && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800 font-medium">활성화 실패</p>
            <p className="mt-1 text-sm text-red-700 leading-relaxed">{error.message}</p>
            {error.code && (
              <p className="mt-2 text-xs text-red-500 font-mono">코드: {error.code}</p>
            )}
          </div>
        )}

        {result && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-md">
            <p className="text-sm text-green-800 font-medium">활성화 성공</p>
            <div className="mt-3 space-y-2 text-sm">
              <div>
                <span className="text-neutral-600">등급: </span>
                <span className="font-medium">
                  {result.levelLabel ?? result.level ?? '사용자 지정'}
                </span>
              </div>
              <div>
                <span className="text-neutral-600">만료: </span>
                <span className="font-medium">
                  {result.expiresAt
                    ? new Date(result.expiresAt).toLocaleDateString('ko-KR')
                    : '영구'}
                </span>
              </div>
              <div>
                <span className="text-neutral-600">Features: </span>
                <span className="font-medium font-mono">{result.features.join(', ')}</span>
              </div>
              <div>
                <span className="text-neutral-600">Activation ID: </span>
                <span className="font-mono text-xs">{result.jti}</span>
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-neutral-800 mb-2">
                Activation Token (이걸 aisight 서버에 입력)
              </label>
              <textarea
                value={result.activationToken}
                readOnly
                rows={5}
                className="w-full px-3 py-2 font-mono text-xs bg-surface border border-neutral-300 rounded-md"
              />
              <button
                type="button"
                onClick={copyToken}
                className="mt-2 px-3 py-1.5 bg-neutral-200 hover:bg-neutral-300 text-neutral-800 text-sm font-medium rounded-md"
              >
                {copied ? '복사됨!' : '클립보드에 복사'}
              </button>
            </div>
            <p className="mt-3 text-xs text-neutral-600">
              고객사 서버에서:{' '}
              <code className="px-1.5 py-0.5 bg-neutral-100 rounded">
                imctl license install &lt;token&gt;
              </code>
            </p>
          </div>
        )}

        <footer className="mt-8 pt-4 border-t border-neutral-100 text-xs text-neutral-500">
          {viewer?.kind === 'customer' ? (
            <Link to="/customer" className="hover:text-neutral-800">
              ← 고객 홈
            </Link>
          ) : (
            <Link to="/admin" className="hover:text-neutral-800">
              ← 관리자 홈
            </Link>
          )}
        </footer>
      </div>
    </div>
  );
}
